import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { searchCollectibles } from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Search, Loader2, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (query.length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    searchCollectibles(query)
      .then(({ data }) => setResults(data.results || []))
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, [query]);

  return (
    <div className="min-h-screen bg-background" data-testid="search-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-8">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <Search className="w-6 h-6 text-gold" />
          <h1 className="font-display text-2xl md:text-3xl font-bold" data-testid="search-page-title">
            Results for "<span className="text-gold">{query}</span>"
          </h1>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-gold animate-spin" />
          </div>
        ) : results.length === 0 ? (
          <div className="text-center py-20" data-testid="search-empty">
            <Search className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-lg text-muted-foreground mb-2">No loot found</p>
            <p className="text-sm text-muted-foreground/60">Try different keywords like "gold", "silver", "coin", or "bar"</p>
            <Link to="/gold">
              <Button variant="outline" className="mt-6 rounded-full">Browse Gold</Button>
            </Link>
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-6" data-testid="search-count">{results.length} collectible{results.length !== 1 ? 's' : ''} found</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {results.map((item) => (
                <Link
                  key={item.collectible_id}
                  to={`/collectible/${item.collectible_id}`}
                  className="group bg-card border border-border/30 rounded-2xl overflow-hidden hover:border-gold/30 transition-all duration-300"
                  data-testid={`search-item-${item.collectible_id}`}
                >
                  <div className="aspect-square bg-secondary/30 overflow-hidden">
                    <img
                      src={item.image_url}
                      alt={item.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <p className="font-display text-base font-semibold truncate group-hover:text-gold transition-colors">{item.name}</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${item.metal_type === 'gold' ? 'bg-gold/15 text-gold' : 'bg-white/10 text-white/70'}`}>
                        {item.metal_type}
                      </span>
                      <span className="text-xs text-muted-foreground">{item.weight}g &middot; {item.purity}</span>
                    </div>
                    <p className="text-xs text-muted-foreground/60 mt-2 line-clamp-2">{item.description}</p>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-xs text-muted-foreground">{item.quantity_remaining}/{item.quantity_total} left</span>
                      <span className="text-xs text-gold font-mono">{item.drop_name}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
